import type { Feature, FeatureCollection } from 'geojson';
import type { GeoJSONSource, GeoJSONSourceRaw, Map } from 'mapbox-gl';

type UseDirectionLayersReturn = {
  setLayers: (
    startingPointCoordinates: Coordinates,
    arrivalPointCoordinates: Coordinates
  ) => Promise<void>;
  showLayers: () => void;
  hideLayers: () => void;
};

export function useDirectionLayers(
  map: Ref<Map | undefined>
): UseDirectionLayersReturn {
  const { theme } = useClientStore().clientState;

  const directionFeatures = ref<Feature[]>([]);
  const directionSourceId = 'direction-source';
  const pointLayerId = 'point-layer';

  function syncDirectionSource(): void {
    if (!map.value) return;

    const source: GeoJSONSourceRaw = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: directionFeatures.value,
      },
    };

    if (!map.value.getSource(directionSourceId)) {
      map.value.addSource(directionSourceId, source);
    } else {
      (map.value.getSource(directionSourceId) as GeoJSONSource)?.setData(
        source.data as FeatureCollection
      );
    }
  }

  function updateDirectionFeatures(
    startingPointCoordinates: Coordinates,
    arrivalPointCoordinates: Coordinates
  ): void {
    directionFeatures.value = [
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: startingPointCoordinates,
        },
        properties: {
          id: 'starting-point',
        },
      },
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: arrivalPointCoordinates,
        },
        properties: {
          id: 'arrival-point',
        },
      },
    ];
  }

  async function setLayers(
    startingPointCoordinates: Coordinates,
    arrivalPointCoordinates: Coordinates
  ): Promise<void> {
    await until(map).not.toBeUndefined();

    if (!map.value) return;

    updateDirectionFeatures(startingPointCoordinates, arrivalPointCoordinates);
    syncDirectionSource();

    if (!map.value.getLayer(pointLayerId)) {
      map.value.addLayer({
        id: pointLayerId,
        type: 'circle',
        source: directionSourceId,
        paint: {
          'circle-radius': 6,
          'circle-stroke-width': 2,
          'circle-color': [
            'case',
            ['==', ['get', 'id'], 'starting-point'],
            theme.colorPrimary,
            ['==', ['get', 'id'], 'arrival-point'],
            theme.colorNeutralDark4,
            theme.colorNeutral, // default color if none match
          ],

          'circle-stroke-color': theme.colorNeutralLight5,
        },
      });
    } else {
      map.value.setLayoutProperty(pointLayerId, 'visibility', 'visible');
    }
  }

  function updateLayersVisibility(visibility: 'visible' | 'none'): void {
    if (!map.value) return;

    if (map.value.getLayer(pointLayerId)) {
      map.value.setLayoutProperty(pointLayerId, 'visibility', visibility);
    }
  }

  function showLayers(): void {
    updateLayersVisibility('visible');
  }

  function hideLayers(): void {
    updateLayersVisibility('none');
  }

  return {
    setLayers,
    showLayers,
    hideLayers,
  };
}
